import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { X, Search } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { ThemedText, Button, Input } from './';
import { ICON_PICKER_LIBRARY, CATEGORY_ICONS, LUCIDE_ICON_MAP, renderFormIcon } from '../utils/iconUtils';

const PickerOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing.lg};
`;

const PickerModal = styled.div`
  background-color: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius.lg};
  border: 1px solid ${props => props.theme.colors.border};
  box-shadow: ${props => props.theme.shadows.lg};
  width: 90vw;
  max-width: 1000px;
  height: 85vh;
  max-height: 700px;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 768px) {
    width: 95vw;
    height: 90vh;
  }
`;

const PickerHeader = styled.div`
  padding: ${props => props.theme.spacing.lg};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PickerContent = styled.div`
  flex: 1;
  display: flex;
  overflow: hidden;
`;

const Sidebar = styled.div`
  width: 280px;
  background-color: ${props => props.theme.colors.backgroundSecondary};
  border-right: 1px solid ${props => props.theme.colors.border};
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 768px) {
    width: 240px;
  }
`;

const SidebarHeader = styled.div`
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  background-color: ${props => props.theme.colors.background};
`;

const SearchContainer = styled.div`
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
`;

const CategoryList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${props => props.theme.spacing.sm} 0;
`;

const CategoryItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  background: ${props => props.active ? props.theme.colors.tint + '15' : 'transparent'};
  border: none;
  border-right: 3px solid ${props => props.active ? props.theme.colors.tint : 'transparent'};
  color: ${props => props.active ? props.theme.colors.tint : props.theme.colors.text};
  text-align: left;
  cursor: pointer;
  font-size: ${props => props.theme.typography.sizes.default};
  transition: all 0.2s ease;

  &:hover {
    background-color: ${props => props.theme.colors.tint}10;
    color: ${props => props.theme.colors.tint};
  }
`;

const CategoryIconWrap = styled.span`
  width: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CategoryName = styled.div`
  flex: 1;
  font-weight: ${props => props.active ? '600' : '400'};
`;

const CategoryCount = styled.span`
  font-size: ${props => props.theme.typography.sizes.small};
  color: ${props => props.theme.colors.muted};
  background-color: ${props => props.theme.colors.border};
  padding: 2px 6px;
  border-radius: ${props => props.theme.borderRadius.sm};
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const MainHeader = styled.div`
  padding: ${props => props.theme.spacing.lg};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  background-color: ${props => props.theme.colors.background};
`;

const IconGrid = styled.div`
  flex: 1;
  padding: ${props => props.theme.spacing.lg};
  overflow-y: auto;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  gap: ${props => props.theme.spacing.md};
  align-content: start;
`;

const IconItem = styled.button`
  width: 80px;
  height: 80px;
  background: ${props => props.selected ? props.theme.colors.tint + '20' : 'transparent'};
  border: 2px solid ${props => props.selected ? props.theme.colors.tint : props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.lg};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  cursor: pointer;
  color: ${props => props.selected ? props.theme.colors.tint : props.theme.colors.text};
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    border-color: ${props => props.theme.colors.tint};
    background-color: ${props => props.theme.colors.tint}10;
    transform: scale(1.05);
    box-shadow: ${props => props.theme.shadows.md};
    color: ${props => props.theme.colors.tint};
  }

  &:active {
    transform: scale(0.95);
  }
`;

const IconLabel = styled.span`
  font-size: 9px;
  max-width: 70px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  opacity: 0.6;
`;

const PickerFooter = styled.div`
  padding: ${props => props.theme.spacing.lg};
  border-top: 1px solid ${props => props.theme.colors.border};
  display: flex;
  justify-content: flex-end;
  gap: ${props => props.theme.spacing.md};
`;

const NoResults = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing.xl};
  color: ${props => props.theme.colors.muted};
`;

const SelectedPreview = styled.div`
  padding: ${props => props.theme.spacing.sm};
  background: ${props => props.theme.colors.tint}20;
  border-radius: ${props => props.theme.borderRadius.md};
  border: 2px solid ${props => props.theme.colors.tint};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  color: ${props => props.theme.colors.tint};
`;

export const IconPicker = ({ isOpen, onClose, onSelect, selectedIcon = 'clipboard-list' }) => {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('Forms & Documents');
  const [currentSelection, setCurrentSelection] = useState(selectedIcon);

  // Build flat searchable list
  const iconData = useMemo(() => {
    const data = [];
    Object.entries(ICON_PICKER_LIBRARY).forEach(([category, icons]) => {
      icons.forEach(iconObj => {
        data.push({ ...iconObj, category });
      });
    });
    return data;
  }, []);

  const getCategoryIconCount = (category) => ICON_PICKER_LIBRARY[category]?.length || 0;

  // Filter icons based on search and category
  const filteredIcons = useMemo(() => {
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return iconData.filter(({ name, keywords, category }) =>
        category.toLowerCase().includes(searchLower) ||
        name.toLowerCase().includes(searchLower) ||
        keywords.some(kw => kw.toLowerCase().includes(searchLower))
      );
    }
    return iconData.filter(({ category }) => category === activeCategory);
  }, [iconData, searchTerm, activeCategory]);

  const handleSelect = () => {
    onSelect(currentSelection);
    onClose();
  };

  const handleCancel = () => {
    setCurrentSelection(selectedIcon);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <PickerOverlay theme={theme} onClick={onClose}>
      <PickerModal theme={theme} onClick={(e) => e.stopPropagation()}>
        <PickerHeader theme={theme}>
          <div>
            <ThemedText type="subtitle">Choose Form Icon</ThemedText>
            <ThemedText type="small" style={{ color: theme.colors.muted }}>
              Select an icon to represent your form
            </ThemedText>
          </div>
          <Button variant="secondary" size="sm" onClick={handleCancel}>
            <X size={14} />
          </Button>
        </PickerHeader>

        <PickerContent>
          {/* Sidebar with categories */}
          <Sidebar theme={theme}>
            <SidebarHeader theme={theme}>
              <ThemedText type="defaultSemiBold" style={{ marginBottom: theme.spacing.sm }}>
                Categories
              </ThemedText>
              <SearchContainer theme={theme}>
                <Input
                  placeholder="Search icons..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ marginBottom: 0 }}
                />
              </SearchContainer>
            </SidebarHeader>

            <CategoryList theme={theme}>
              {Object.keys(ICON_PICKER_LIBRARY).map(category => {
                const CatIcon = CATEGORY_ICONS[category];
                return (
                  <CategoryItem
                    key={category}
                    theme={theme}
                    active={activeCategory === category && !searchTerm}
                    onClick={() => {
                      setActiveCategory(category);
                      setSearchTerm('');
                    }}
                  >
                    <CategoryIconWrap>
                      {CatIcon ? <CatIcon size={16} /> : null}
                    </CategoryIconWrap>
                    <CategoryName active={activeCategory === category && !searchTerm}>
                      {category}
                    </CategoryName>
                    <CategoryCount theme={theme}>
                      {getCategoryIconCount(category)}
                    </CategoryCount>
                  </CategoryItem>
                );
              })}
            </CategoryList>
          </Sidebar>

          {/* Main content with icons */}
          <MainContent>
            <MainHeader theme={theme}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <ThemedText type="defaultSemiBold">
                    {searchTerm ? 'Search Results' : activeCategory}
                  </ThemedText>
                  <ThemedText type="small" style={{ color: theme.colors.muted }}>
                    {filteredIcons.length} {filteredIcons.length === 1 ? 'icon' : 'icons'} available
                  </ThemedText>
                </div>
                {currentSelection && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.md }}>
                    <ThemedText type="small" style={{ color: theme.colors.muted }}>
                      Selected:
                    </ThemedText>
                    <SelectedPreview theme={theme}>
                      {renderFormIcon(currentSelection, 24)}
                    </SelectedPreview>
                  </div>
                )}
              </div>
            </MainHeader>

            <IconGrid theme={theme}>
              {filteredIcons.length === 0 ? (
                <NoResults theme={theme} style={{ gridColumn: '1 / -1', textAlign: 'center', padding: theme.spacing.xl }}>
                  <div style={{ marginBottom: theme.spacing.lg, opacity: 0.5 }}>
                    <Search size={48} />
                  </div>
                  <ThemedText type="subtitle" style={{ marginBottom: theme.spacing.md }}>
                    No icons found
                  </ThemedText>
                  <ThemedText type="small" style={{ color: theme.colors.muted }}>
                    {searchTerm ? 'Try a different search term or browse categories' : 'This category appears to be empty'}
                  </ThemedText>
                </NoResults>
              ) : (
                filteredIcons.map(({ name, component: IconComp, category }, index) => (
                  <IconItem
                    key={`${category}-${name}-${index}`}
                    theme={theme}
                    selected={currentSelection === name}
                    onClick={() => setCurrentSelection(name)}
                    title={name}
                  >
                    <IconComp size={28} />
                    <IconLabel>{name}</IconLabel>
                  </IconItem>
                ))
              )}
            </IconGrid>
          </MainContent>
        </PickerContent>

        <PickerFooter theme={theme}>
          <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.lg }}>
            <ThemedText type="small" style={{ color: theme.colors.muted }}>
              Click an icon to select it for your form
            </ThemedText>
          </div>
          <div style={{ display: 'flex', gap: theme.spacing.md }}>
            <Button variant="secondary" onClick={handleCancel}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSelect} disabled={!currentSelection}>
              Select Icon
            </Button>
          </div>
        </PickerFooter>
      </PickerModal>
    </PickerOverlay>
  );
};
